"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _components = require("@folio/stripes/components");
var _core = require("@folio/stripes/core");
var _hooks = require("../hooks");
var _ActionList = _interopRequireDefault(require("../ActionList"));
var _validators = require("../validators");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const propTypes = {
  afterQueryCalls: _propTypes.default.object,
  allowSpecial: _propTypes.default.bool,
  catchQueryCalls: _propTypes.default.object,
  desc: _propTypes.default.string,
  displayConditions: _propTypes.default.shape({
    create: _propTypes.default.bool,
    delete: _propTypes.default.bool,
    view: _propTypes.default.bool
  }),
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node]),
  labelOverrides: _propTypes.default.object,
  refdataEndpoint: _propTypes.default.string
};
const EditableRefdataList = _ref => {
  let {
    afterQueryCalls,
    allowSpecial = false,
    // special characters will be directly stripped out of the value before it is sent to the backend
    catchQueryCalls,
    desc,
    /*
     * Set of extra booleans for controlling access to actions
     * edit/create/delete (View should be handled externally)
     * This will not overwrite "internal" behaviour, ie setting
     * delete to 'true' here would still not render a delete button
     * for an internal refdata value
     */
    displayConditions = {
      create: true,
      edit: true,
      delete: true
    },
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    label,
    labelOverrides = {},
    // An object containing translation alternatives
    refdataEndpoint
  } = _ref;
  const {
    create: createCondition = true,
    delete: deleteCondition = true,
    edit: editCondition = true
  } = displayConditions;

  /* A component that allows for editing of refdata values */
  const callout = (0, _react.useContext)(_core.CalloutContext);
  const kintIntl = (0, _hooks.useKintIntl)(passedIntlKey, passedIntlNS);

  // fetch refdata values
  const {
    data: {
      0: refdata
    } = {},
    isLoading: isRefdataLoading
  } = (0, _hooks.useRefdata)({
    desc,
    endpoint: refdataEndpoint,
    returnQueryObject: true
  });
  const [contentData, setContentData] = (0, _react.useState)([]);
  const [deleteModal, setDeleteModal] = (0, _react.useState)({
    visible: false,
    refdata: null
  });
  const sortByLabel = (a, b) => a.label.localeCompare(b.label);
  (0, _react.useEffect)(() => {
    if (!isRefdataLoading) {
      setContentData(refdata?.values?.sort(sortByLabel) ?? []);
    }
  }, [isRefdataLoading, refdata]);

  // Edit and Create will use the same PUT mutation
  const {
    delete: deleteRefdataValue,
    put: editRefdataValue
  } = (0, _hooks.useMutateRefdataValue)({
    afterQueryCalls: {
      delete: json => {
        setContentData(json?.values?.sort(sortByLabel) ?? []);
        if (afterQueryCalls?.delete) {
          afterQueryCalls.delete(json);
        }
      },
      put: json => {
        setContentData(json?.values?.sort(sortByLabel) ?? []);
        if (afterQueryCalls?.put) {
          afterQueryCalls.put(json);
        }
      }
    },
    catchQueryCalls: {
      // Default delete behaviour is to fire a callout, either with kint-components default message
      // or one provided in labelOverrides, which is passed the error message and refdata in question
      delete: async err => {
        const errorResp = await (0, _utils.parseErrorResponse)(err.response);
        callout.sendCallout({
          message: kintIntl.formatKintMessage({
            id: 'refdata.deleteRefdataValue.errorMessage',
            overrideValue: labelOverrides?.deleteError
          }, {
            label: deleteModal?.refdata?.label,
            error: errorResp?.message
          }),
          type: 'error'
        });
      },
      ...catchQueryCalls // override defaults here
    },
    endpoint: refdataEndpoint,
    id: refdata?.id,
    queryParams: {
      delete: {
        enabled: !!refdata
      },
      put: {
        enabled: !!refdata
      }
    }
  });
  if (isRefdataLoading) {
    return 'loading';
  }

  // This is the function which will take a row in the table and assign the relevant actions to it
  const actionAssigner = () => {
    const actionArray = [];
    if (editCondition) {
      actionArray.push({
        name: 'edit',
        label: kintIntl.formatKintMessage({
          id: 'edit',
          overrideValue: labelOverrides?.edit
        }),
        icon: 'edit',
        callback: data => editRefdataValue(data),
        ariaLabel: data => kintIntl.formatKintMessage({
          id: 'refdata.editAriaLabel',
          overrideValue: labelOverrides?.editAriaLabel
        }, {
          label: data?.label
        })
      });
    }
    if (!refdata?.internal && deleteCondition) {
      actionArray.push({
        name: 'delete',
        label: kintIntl.formatKintMessage({
          id: 'delete',
          overrideValue: labelOverrides?.delete
        }),
        icon: 'trash',
        callback: data => setDeleteModal({
          visible: true,
          refdata: data
        }),
        ariaLabel: data => kintIntl.formatKintMessage({
          id: 'refdata.deleteAriaLabel',
          overrideValue: labelOverrides?.deleteAriaLabel
        }, {
          label: data?.label
        })
      });
    }
    return actionArray;
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_ActionList.default, {
      actionAssigner: actionAssigner,
      columnMapping: {
        label: kintIntl.formatKintMessage({
          id: 'refdata.label',
          overrideValue: labelOverrides?.label
        }),
        value: kintIntl.formatKintMessage({
          id: 'refdata.value',
          overrideValue: labelOverrides?.value
        })
      },
      contentData: contentData,
      creatableFields: {
        value: () => false
      },
      createCallback: !createCondition || refdata?.internal ? null : data => {
        if (allowSpecial) {
          editRefdataValue(data);
        } else {
          editRefdataValue({
            ...data,
            value: (0, _utils.selectorSafe)(data?.label)?.replaceAll('%20', ' ')
          });
        }
      },
      editableFields: {
        value: () => false
      },
      hideActionsColumn: !deleteCondition && !editCondition,
      hideCreateButton: !createCondition,
      label: label,
      validateFields: {
        label: _validators.required
      },
      visibleFields: ['label', 'value']
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.ConfirmationModal, {
      confirmLabel: kintIntl.formatKintMessage({
        id: 'delete',
        overrideValue: labelOverrides?.delete
      }),
      heading: kintIntl.formatKintMessage({
        id: 'refdata.deleteRefdataValue',
        overrideValue: labelOverrides?.deleteRefdataValue
      }),
      message: kintIntl.formatKintMessage({
        id: 'refdata.deleteRefdataValue.confirmMessage',
        overrideValue: labelOverrides?.deleteRefdataValueMessage
      }, {
        name: deleteModal?.refdata?.label
      }),
      onCancel: () => setDeleteModal({
        visible: false,
        refdata: null
      }),
      onConfirm: () => {
        deleteRefdataValue(deleteModal?.refdata?.id);
        setDeleteModal({
          visible: false,
          refdata: null
        });
      },
      open: deleteModal?.visible
    })]
  });
};
EditableRefdataList.propTypes = propTypes;
var _default = exports.default = EditableRefdataList;